import React from "react";
import {
  Button,
  DataTable,
  Skeleton,
  Tag,
  cn,
} from "@jem-open/jem-ui";
import { ColumnDef } from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight, Eye } from "lucide-react";
import { SyncRun } from "../services/model/SyncRun";
import { CHANNEL_LABELS, SYNC_STATUS_TAG } from "../constants";

interface SyncRunsTableComponentProps {
  runs: SyncRun[];
  isLoading?: boolean;
  sortCol?: string;
  sortDir?: "asc" | "desc";
  onSortChange?: (col: string, dir: "asc" | "desc") => void;
  onRunClick?: (run: SyncRun) => void;
  page?: number;
  totalPages?: number;
  totalCount?: number;
  onPageChange?: (page: number) => void;
}

const getSortIcon = (dir: "asc" | "desc" | false): JSX.Element => {
  if (dir === "asc") return <ArrowUp size={14} />;
  if (dir === "desc") return <ArrowDown size={14} />;
  return <ArrowUpDown size={14} />;
};

const SortHeader = ({
  colKey,
  title,
  sortCol,
  sortDir,
  onSortChange,
}: {
  colKey: string;
  title: string;
  sortCol?: string;
  sortDir?: "asc" | "desc";
  onSortChange?: (col: string, dir: "asc" | "desc") => void;
}): JSX.Element => {
  const isSorted = sortCol === colKey;
  const nextDir = isSorted && sortDir === "asc" ? "desc" : "asc";
  return (
    <button
      type="button"
      onClick={() => onSortChange?.(colKey, nextDir)}
      className="flex items-center gap-xxs text-xs font-semibold text-greyscale-text-caption hover:text-greyscale-text-title"
    >
      {title}
      {getSortIcon(isSorted ? (sortDir ?? false) : false)}
    </button>
  );
};


const getChannelLabel = (channel: string | undefined): string =>
  (channel && CHANNEL_LABELS[channel]) || "-";

const makeSkeletonRows = (count: number): SyncRun[] =>
  Array.from({ length: count }, (_, i) => ({ id: String(i) } as SyncRun));

const makeSyncColumns = (
  isLoading: boolean,
  sortCol: string | undefined,
  sortDir: "asc" | "desc" | undefined,
  onSortChange: ((col: string, dir: "asc" | "desc") => void) | undefined,
  onRunClick: ((run: SyncRun) => void) | undefined
): ColumnDef<SyncRun>[] => [
  {
    accessorKey: "start",
    enableSorting: false,
    header: () => (
      <SortHeader colKey="start" title="Time" sortCol={sortCol} sortDir={sortDir} onSortChange={onSortChange} />
    ),
    cell: ({ row }) =>
      isLoading ? (
        <Skeleton className="h-4 w-32" />
      ) : (
        <span className="text-sm text-greyscale-text-body">
          {new Date(row.original.start).toLocaleString("en-ZA", {
            dateStyle: "medium",
            timeStyle: "short",
          })}
        </span>
      ),
  },
  {
    accessorKey: "creations",
    enableSorting: false,
    header: () => (
      <SortHeader colKey="creations" title="Created" sortCol={sortCol} sortDir={sortDir} onSortChange={onSortChange} />
    ),
    cell: ({ row }) =>
      isLoading ? (
        <Skeleton className="h-4 w-8" />
      ) : (
        <span className="text-sm font-semibold text-greyscale-text-title">
          {row.original.creations.toLocaleString()}
        </span>
      ),
  },
  {
    accessorKey: "updates",
    enableSorting: false,
    header: () => (
      <SortHeader colKey="updates" title="Updated" sortCol={sortCol} sortDir={sortDir} onSortChange={onSortChange} />
    ),
    cell: ({ row }) =>
      isLoading ? (
        <Skeleton className="h-4 w-8" />
      ) : (
        <span className="text-sm font-semibold text-greyscale-text-title">
          {row.original.updates.toLocaleString()}
        </span>
      ),
  },
  {
    accessorKey: "blocked",
    enableSorting: false,
    header: () => (
      <SortHeader colKey="blocked" title="Blocked" sortCol={sortCol} sortDir={sortDir} onSortChange={onSortChange} />
    ),
    cell: ({ row }) =>
      isLoading ? (
        <Skeleton className="h-4 w-8" />
      ) : (
        <span
          className={cn(
            "text-sm font-semibold",
            row.original.blocked > 0
              ? "text-secondary-pink-900"
              : "text-greyscale-text-caption"
          )}
        >
          {row.original.blocked}
        </span>
      ),
  },
  {
    accessorKey: "unblocked",
    enableSorting: false,
    header: () => (
      <SortHeader colKey="unblocked" title="Unblocked" sortCol={sortCol} sortDir={sortDir} onSortChange={onSortChange} />
    ),
    cell: ({ row }) =>
      isLoading ? (
        <Skeleton className="h-4 w-8" />
      ) : (
        <span className="text-sm font-semibold text-greyscale-text-title">
          {row.original.unblocked}
        </span>
      ),
  },
  {
    accessorKey: "channel",
    enableSorting: false,
    header: () => (
      <SortHeader colKey="channel" title="Channel" sortCol={sortCol} sortDir={sortDir} onSortChange={onSortChange} />
    ),
    cell: ({ row }) =>
      isLoading ? (
        <Skeleton className="h-4 w-20" />
      ) : (
        <span className="text-sm text-greyscale-text-body">
          {getChannelLabel(row.original.channel)}
        </span>
      ),
  },
  {
    accessorKey: "status",
    enableSorting: false,
    header: () => (
      <SortHeader colKey="status" title="Status" sortCol={sortCol} sortDir={sortDir} onSortChange={onSortChange} />
    ),
    cell: ({ row }) => {
      if (isLoading) return <Skeleton className="h-5 w-24" />;
      const meta = SYNC_STATUS_TAG[row.original.status] ?? {
        label: row.original.status,
        variant: "default" as const,
      };
      return (
        <Tag variant={meta.variant} size="sm">
          {meta.label}
        </Tag>
      );
    },
  },
  {
    id: "actions",
    enableSorting: false,
    header: () => <span className="sr-only">Actions</span>,
    cell: ({ row }) => {
      if (isLoading) return null;
      return (
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRunClick?.(row.original)}
            aria-label={`View details for ${new Date(row.original.start).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" })}`}
          >
            <Eye size={14} />
          </Button>
        </div>
      );
    },
  },
];

const SyncRunsTableComponent: React.FC<SyncRunsTableComponentProps> = ({
  runs,
  isLoading = false,
  sortCol,
  sortDir,
  onSortChange,
  onRunClick,
  page = 1,
  totalPages = 1,
  totalCount = 0,
  onPageChange,
}) => {
  const PAGE_SIZE = 10;
  const clampedPage = Math.max(1, Math.min(page, totalPages || 1));

  const columns = makeSyncColumns(isLoading, sortCol, sortDir, onSortChange, onRunClick);
  const tableData = isLoading ? makeSkeletonRows(PAGE_SIZE) : runs;

  return (
    <div>
      <DataTable
        columns={columns}
        data={tableData}
        showPagination={false}
        showToolbar={false}
      />

      <div className="flex items-center justify-between mt-sm">
        <span className="text-sm text-greyscale-text-caption">
          {totalCount > 0
            ? `${(clampedPage - 1) * PAGE_SIZE + 1}–${Math.min(clampedPage * PAGE_SIZE, totalCount)} of ${totalCount}`
            : "0 results"}
        </span>

        {totalPages > 1 && (
          <div className="flex items-center gap-xs">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange?.(Math.max(1, clampedPage - 1))}
              disabled={isLoading || clampedPage === 1}
              aria-label="Previous page"
            >
              <ChevronLeft size={14} />
            </Button>
            <span className="text-sm text-greyscale-text-caption px-xs">
              Page {clampedPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange?.(Math.min(totalPages, clampedPage + 1))}
              disabled={isLoading || clampedPage === totalPages}
              aria-label="Next page"
            >
              <ChevronRight size={14} />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SyncRunsTableComponent;
